import {
  DynamicDocumentGroup,
  DynamicDocumentItem,
  DynamicDocumentItemData,
  InstallationModuleData,
} from '@/types';
import { matchesSerialPattern } from '@/helpers/serial-number';

import { isItemRequired } from './required';

/**
 * The `depends` evaluator, shared by the installation delivery documents and the assignment
 * questionnaires. It lived inside the deliveryDocuments store module, which made it reachable only
 * through a getter keyed on an installation id and a module type — so the service screen, which has
 * neither, silently ignored every `depends` in its template.
 *
 * `moduleData` stays optional and last so the installation call sites read exactly as they did.
 */
export function isItemVisible(
  items: DynamicDocumentItem[],
  data: DynamicDocumentItemData[],
  moduleData?: InstallationModuleData
) {
  return function isVisible(item: DynamicDocumentItem | undefined): boolean {
    if (!item?.depends) return true;

    const otherItem = items.find(i => i.field === item.depends?.field)!;

    if (!isVisible(otherItem)) {
      return false;
    }

    const itemData = data.find(d => d.field === otherItem.field);

    const otherValue = itemData?.value ?? moduleData?.[item.depends.field];

    if (Array.isArray(otherValue)) {
      return otherValue.includes(item.depends.value);
    } else {
      return item.depends.value === otherValue;
    }
  };
}

export function isGroupVisible(
  group: DynamicDocumentGroup,
  groups: DynamicDocumentGroup[],
  data: DynamicDocumentItemData[],
  moduleData?: InstallationModuleData
): boolean {
  if (!group.depends) return true;

  const otherItem = groups
    .flatMap(g => g.items)
    .find(item => {
      return item.field === group.depends?.field;
    });

  if (!otherItem) {
    return false;
  }
  const isVisible = isItemVisible([otherItem], data, moduleData);

  if (!isVisible(otherItem)) {
    return false;
  }

  const itemData = data.find(d => d.field === otherItem.field);

  const otherValue = itemData?.value ?? moduleData?.[group.depends.field];

  if (Array.isArray(otherValue)) {
    return otherValue.includes(group.depends.value);
  } else {
    return group.depends.value === otherValue;
  }
}

/**
 * The template as the monteur should see it right now: groups and items whose `depends` are
 * satisfied, with emptied-out groups dropped. A template with no `depends` anywhere comes back
 * unchanged, which is what keeps the existing service vragenlijst byte-identical.
 */
export function visibleDocument(
  template: DynamicDocumentGroup[],
  data: DynamicDocumentItemData[]
): DynamicDocumentGroup[] {
  const items = template.flatMap(group => group.items);

  return template
    .filter(group => isGroupVisible(group, template, data))
    .map(group => ({ ...group, items: group.items.filter(isItemVisible(items, data)) }))
    .filter(group => group.items.length > 0);
}

/**
 * The answer keys a questionnaire persists as scalars in `questionnaire_data`.
 *
 * IMAGE is excluded because photos are real Documents uploaded out-of-band; HEADER and WARNING
 * because they are copy, not questions. SIGNATURE is deliberately included — its `[naam, dataURI]`
 * pair is an answer and rides along with the rest.
 */
export function scalarFields(template: DynamicDocumentGroup[]): string[] {
  return template
    .flatMap(group => group.items)
    .filter(item => !['IMAGE', 'HEADER', 'WARNING'].includes(item.type))
    .map(item => item.field as string);
}

/**
 * Whether an item counts as answered, for the Afronden gate and the per-item tick.
 *
 * A hidden item is always complete: a question the monteur cannot see must never be able to block
 * finishing. Requiredness is asked with the FULL answer array so a cross-field
 * `required: {field, value}` resolves — passing only the item's own row, as the renderers do, makes
 * every conditional requirement read false.
 */
export function isItemComplete(
  item: DynamicDocumentItem,
  data: DynamicDocumentItemData[],
  template: DynamicDocumentGroup[]
): boolean {
  const items = template.flatMap(group => group.items);

  if (!isItemVisible(items, data)(item)) {
    return true;
  }

  if (!isItemRequired(item, data)) {
    return true;
  }

  const value = data.find(d => d.field === item.field)?.value;

  if (item.type === 'IMAGE') {
    return Array.isArray(value) && value.length > 0;
  }

  if (item.type === 'SIGNATURE') {
    return Array.isArray(value) && !!value[0] && !!value[1];
  }

  // The manager seeds an empty placeholder row, so length alone proves nothing. Nor does a row
  // that is filled: a serial the question's format rules out is one the CRM refuses at the finish.
  if (item.type === 'SERIAL') {
    return (
      Array.isArray(value) &&
      (value as { serialNumber?: string }[]).some(entry =>
        matchesSerialPattern(entry?.serialNumber, item.meta?.patterns)
      )
    );
  }

  if (item.type === 'BOOL') {
    return typeof value === 'boolean';
  }

  return typeof value === 'string' && value.trim().length > 0;
}

/** Stable DOM id for a section, so the footer tabs can scroll to the panel they name. */
export function sectionAnchorId(title: string): string {
  return `section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
}

export type SectionStatus = {
  /** Panel index, matching the order Document.vue renders `visibleDocument` in. */
  index: number;
  nav: string;
  icon?: string;
  anchor: string;
  open: number;
};

/**
 * One entry per section the monteur can see, with how many of its questions are still open — what
 * colours the footer tabs and the panel headers.
 */
export function sectionStatuses(
  template: DynamicDocumentGroup[],
  data: DynamicDocumentItemData[]
): SectionStatus[] {
  return visibleDocument(template, data).map((group, index) => ({
    index,
    nav: group.nav ?? group.title,
    icon: group.icon,
    anchor: sectionAnchorId(group.title),
    open: group.items.filter(item => !isItemComplete(item, data, template)).length,
  }));
}

/**
 * Which section the monteur is looking at: the last one whose header has passed `threshold`.
 *
 * `topOf` yields a section's current distance from the top of the viewport, or undefined when it
 * has no element yet. Falls back to the first section, so there is always an anchor to follow —
 * before any scrolling has happened, that is the one on screen.
 */
export function activeSection(
  sections: SectionStatus[],
  topOf: (section: SectionStatus) => number | undefined,
  threshold: number
): string | undefined {
  let active = sections[0]?.anchor;

  sections.forEach(section => {
    const top = topOf(section);

    if (top !== undefined && top <= threshold) {
      active = section.anchor;
    }
  });

  return active;
}

/**
 * Every visible required item still unanswered, in template order — the Afronden gate's list.
 */
export function openPoints(
  template: DynamicDocumentGroup[],
  data: DynamicDocumentItemData[]
): { field: string; title: string; group: string }[] {
  return template.flatMap(group =>
    group.items
      .filter(item => !isItemComplete(item, data, template))
      .map(item => ({ field: item.field as string, title: item.title, group: group.title }))
  );
}

/**
 * Answers the office should look at before approving: a "Nee" where "Ja" was expected, a damaged
 * installation, a weak signal. Declared per item as `meta.attention: {when, note}`, where `when` is
 * the answer (or answers) that raises it.
 *
 * Unlike openPoints these never block finishing — they are legitimate answers that just need a
 * human to see them, so an unanswered question raises nothing.
 */
export function attentionPoints(
  template: DynamicDocumentGroup[],
  data: DynamicDocumentItemData[]
): { field: string; note: string; answer: string }[] {
  return template.flatMap(group =>
    group.items.flatMap(item => {
      const attention = item.meta?.attention;

      if (!attention || !isItemComplete(item, data, template)) return [];

      const value = data.find(d => d.field === item.field)?.value;
      const triggers = Array.isArray(attention.when) ? attention.when : [attention.when];

      if (!triggers.includes(value)) return [];

      return [
        {
          field: item.field as string,
          note: attention.note,
          answer: typeof value === 'boolean' ? (value ? 'Ja' : 'Nee') : String(value),
        },
      ];
    })
  );
}
